import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  User,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Plus,
  Minus,
  AlertTriangle,
  Accessibility,
  Ticket,
  Utensils,
  ChefHat,
  Home
} from "lucide-react";
import { Button } from "@headlessui/react";

export default function ModalAsignacionGuiada({
  isOpen,
  onClose,
  invitado,
  mesas,
  configuracionTurnos = null,
  onConfirm
}) {
  const [step, setStep] = useState(0);

  // --- PASO 1: Splits (Divisiones de boletos) ---
  const [splits, setSplits] = useState([]);

  // --- PASO 2: Asignación de Mesas por Split ---
  const [mesasSeleccionadas, setMesasSeleccionadas] = useState([]); // Array de mesa.id o mesa.numero

  // --- PASO 3: Menús por Split ---
  // Array de objetos: { todosNormales: boolean, infantil: number, especial: number, celiaco: number, otros: { [key: string]: number } }
  const [menusPorSplit, setMenusPorSplit] = useState([]);

  // --- PASO 4: Detalles, Alergias y Accesibilidad por Split ---
  // Array de objetos: { notas: string, requiereAccesibilidad: boolean }
  const [detallesPorSplit, setDetallesPorSplit] = useState([]);

  // --- PASO 2: Búsqueda de mesa por split ---
  // { [splitIndex]: string }
  const [busquedaMesas, setBusquedaMesas] = useState({});


  // Inicialización de estados al abrir
  useEffect(() => {
    if (isOpen && invitado) {
      setStep(0);
      const totalBoletos = invitado.cantidad || 1;
      
      // Inicializar con 1 solo split con el total de boletos
      setSplits([totalBoletos]);
      setMesasSeleccionadas([""]);
      setMenusPorSplit([{ todosNormales: true, infantil: 0, especial: 0, celiaco: 0, otros: {} }]);
      setDetallesPorSplit([{ notas: "", requiereAccesibilidad: false }]);
    }
  }, [isOpen, invitado]);

  // Lista de mesas disponibles filtradas y ordenadas
  const mesasDisponibles = useMemo(() => {
    if (!mesas) return [];
    return mesas
      .filter((el) => el.type === "mesa" || el.type === "mesaRectangular")
      .map((m) => {
        const ocupados = m.disponibilidad?.asientos_ocupados ?? m.invitados ?? 0;
        const capacidad = m.capacidad || 10;
        const libres = capacidad - ocupados;
        const estaBloqueada = m.disponibilidad?.esta_bloqueada || m.disponibilidad?.bloqueada || m.bloqueada || false;
        const motivoBloqueo = m.disponibilidad?.motivo_bloqueo || m.motivo_bloqueo || "";
        
        return {
          ...m,
          libres,
          ocupados,
          capacidad,
          estaBloqueada,
          motivoBloqueo,
          // ✅ Una mesa está llena solo si sus ocupados >= su capacidad real
          estaLlena: ocupados >= capacidad
        };
      })
      .sort((a, b) => {
        // Ordenar numéricamente por el número de la mesa
        const numA = parseInt(a.numero) || 0;
        const numB = parseInt(b.numero) || 0;
        return numA - numB;
      });
  }, [mesas]);

  // Mapear opciones de menú de la configuración de turnos
  const opcionesMenu = useMemo(() => {
    if (configuracionTurnos?.tipos_menu && configuracionTurnos.tipos_menu.length > 0) {
      return configuracionTurnos.tipos_menu.map(tipo => {
        const tipoId = typeof tipo === 'string' ? tipo : tipo.id || tipo.value;
        const tipoLabel = typeof tipo === 'string' 
          ? tipo.charAt(0).toUpperCase() + tipo.slice(1) 
          : tipo.label || tipo.nombre || tipoId;
        
        return { id: tipoId, label: tipoLabel };
      });
    }
    return [
      { id: "infantil", label: "Infantil" },
      { id: "especial", label: "Especial" },
      { id: "celiaco", label: "Celíaco" }
    ];
  }, [configuracionTurnos]);

  // Opciones de menú especiales (excluye 'normal' porque se calcula automáticamente)
  const opcionesMenuEspeciales = useMemo(
    () => opcionesMenu.filter((opc) => opc.id !== "normal"),
    [opcionesMenu]
  );

  if (!isOpen || !invitado) return null;

  const totalBoletos = invitado.cantidad || 1;
  const sumaSplits = splits.reduce((sum, val) => sum + val, 0);
  // Máximo de boletos por mesa permitido en el sistema
  const MAX_POR_MESA = 12;
  const algutSplitExcede = splits.some(s => s > MAX_POR_MESA);
  const splitsValidos = sumaSplits === totalBoletos && splits.every(s => s > 0 && s <= MAX_POR_MESA);

  // --- Lógica del Paso 1: Splits ---
  const handleAddSplit = () => {
    setSplits([...splits, 0]);
    setMesasSeleccionadas([...mesasSeleccionadas, ""]);
    setMenusPorSplit([...menusPorSplit, { todosNormales: true, infantil: 0, especial: 0, celiaco: 0, otros: {} }]);
    setDetallesPorSplit([...detallesPorSplit, { notas: "", requiereAccesibilidad: false }]);
  };

  const handleRemoveSplit = (index) => {
    if (splits.length === 1) return;
    const newSplits = splits.filter((_, i) => i !== index);
    setSplits(newSplits);
    setMesasSeleccionadas(mesasSeleccionadas.filter((_, i) => i !== index));
    setMenusPorSplit(menusPorSplit.filter((_, i) => i !== index));
    setDetallesPorSplit(detallesPorSplit.filter((_, i) => i !== index));
  };

  const handleSplitValueChange = (index, value) => {
    const intValue = Math.max(0, parseInt(value) || 0);
    const newSplits = [...splits];
    newSplits[index] = intValue;
    setSplits(newSplits);
  };

  // Dividir equitativamente en grupos de N boletos (por defecto 10)
  const aplicarDivisionEquitativa = (capacidadBase = 10) => {
    const partes = [];
    let restante = totalBoletos;
    while (restante > 0) {
      if (restante >= capacidadBase) {
        partes.push(capacidadBase);
        restante -= capacidadBase;
      } else {
        partes.push(restante);
        restante = 0;
      }
    }
    
    setSplits(partes);
    setMesasSeleccionadas(partes.map(() => ""));
    setMenusPorSplit(partes.map(() => ({ todosNormales: true, infantil: 0, especial: 0, celiaco: 0, otros: {} })));
    setDetallesPorSplit(partes.map(() => ({ notas: "", requiereAccesibilidad: false })));
  };

  // --- Lógica del Paso 2: Selección de Mesas ---
  const handleMesaChange = (index, mesaId) => {
    const newMesas = [...mesasSeleccionadas];
    newMesas[index] = mesaId;
    setMesasSeleccionadas(newMesas);
  };

  const mesasValidas = mesasSeleccionadas.every(m => m !== "") && mesasSeleccionadas.length === splits.length;

  // --- Lógica del Paso 3: Menús ---
  const handleToggleTodosNormales = (index, value) => {
    const newMenus = [...menusPorSplit];
    newMenus[index] = {
      ...newMenus[index],
      todosNormales: value,
      infantil: 0,
      especial: 0,
      celiaco: 0,
      otros: {}
    };
    setMenusPorSplit(newMenus);
  };

  const handleMenuCountChange = (index, menuType, change) => {
    const newMenus = [...menusPorSplit];
    const currentMenu = newMenus[index];
    const currentSplitSize = splits[index];

    // Calcular cuántos especiales llevamos asignados en este split (excluyendo normal)
    const otrosAsignados = (currentMenu.infantil || 0) +
                           (currentMenu.especial || 0) +
                           (currentMenu.celiaco || 0) +
                           Object.values(currentMenu.otros || {}).reduce((s, v) => s + v, 0);

    const esEstandard = ["infantil", "especial", "celiaco"].includes(menuType);
    const valorActual = esEstandard ? (currentMenu[menuType] || 0) : (currentMenu.otros?.[menuType] || 0);
    const nuevoValor = Math.max(0, valorActual + change);

    // Validar que la suma total de menús especiales no exceda el tamaño del split
    const diferencia = nuevoValor - valorActual;
    if (otrosAsignados + diferencia > currentSplitSize) {
      return; // No se puede agregar más allá del tamaño del split
    }

    if (esEstandard) {
      newMenus[index] = {
        ...currentMenu,
        [menuType]: nuevoValor
      };
    } else {
      newMenus[index] = {
        ...currentMenu,
        otros: {
          ...(currentMenu.otros || {}),
          [menuType]: nuevoValor
        }
      };
    }
    
    setMenusPorSplit(newMenus);
  };

  // Validar menús del Paso 3 (siempre válidos porque el normal se calcula automáticamente)
  const menusValidos = true;

  // --- Lógica del Paso 4: Detalles y Alergias ---
  const handleNotaChange = (index, valor) => {
    const newDetalles = [...detallesPorSplit];
    newDetalles[index] = { ...newDetalles[index], notas: valor };
    setDetallesPorSplit(newDetalles);
  };

  const handleAccesibilidadChange = (index, valor) => {
    const newDetalles = [...detallesPorSplit];
    newDetalles[index] = { ...newDetalles[index], requiereAccesibilidad: valor };
    setDetallesPorSplit(newDetalles);
  };

  // --- Paso 5: Confirmar y Guardar ---
  const handleConfirmar = () => {
    // Empaquetar datos para confirmar
    const asignacionesFinales = splits.map((cantidad, idx) => {
      const mesaId = mesasSeleccionadas[idx];
      const mesaObj = mesasDisponibles.find(m => m.id === mesaId);
      const menuConfig = menusPorSplit[idx];
      const detalleConfig = detallesPorSplit[idx];

      // Construir array de personas detalladas
      const personasArray = [];
      let boletosRestantes = cantidad;

      // 1. Agregar menús especiales configurados (excluye 'normal' que se llena después)
      opcionesMenuEspeciales.forEach(opc => {
        const count = opc.id === "infantil" ? menuConfig.infantil :
                      opc.id === "especial" ? menuConfig.especial :
                      opc.id === "celiaco" ? menuConfig.celiaco :
                      (menuConfig.otros?.[opc.id] || 0);

        for (let i = 0; i < count; i++) {
          personasArray.push({
            nombre: personasArray.length === 0 ? invitado.nombre : `${invitado.nombre} - Acompañante ${personasArray.length + 1}`,
            tipoMenu: opc.id,
            restricciones: {},
            otraRestriccion: "",
            necesidadesEspeciales: { requiereAccesibilidad: false, comentarios: "" }
          });
          boletosRestantes--;
        }
      });

      // 2. Rellenar el resto con menús normales
      for (let i = 0; i < boletosRestantes; i++) {
        personasArray.push({
          nombre: personasArray.length === 0 ? invitado.nombre : `${invitado.nombre} - Acompañante ${personasArray.length + 1}`,
          tipoMenu: "normal",
          restricciones: {},
          otraRestriccion: "",
          necesidadesEspeciales: { requiereAccesibilidad: false, comentarios: "" }
        });
      }

      // Aplicar restricciones o notas a la primera persona (responsable)
      if (personasArray.length > 0) {
        personasArray[0].otraRestriccion = detalleConfig.notas;
        personasArray[0].necesidadesEspeciales = {
          requiereAccesibilidad: detalleConfig.requiereAccesibilidad,
          comentarios: detalleConfig.requiereAccesibilidad ? "Requiere acceso a silla de ruedas" : ""
        };
      }

      return {
        mesaId,
        numeroMesa: mesaObj?.numero,
        cantidad,
        tipoMesa: mesaObj?.type || "mesa",
        notas: detalleConfig.notas,
        necesidadEspecial: detalleConfig.requiereAccesibilidad,
        personas: personasArray
      };
    });

    onConfirm(asignacionesFinales);
  };

  // Renderizador de indicadores de paso
  const stepsLabels = ["Dividir Boletos", "Elegir Mesas", "Tipos de Menú", "Notas y Alergias", "Confirmar"];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] border border-gray-200 dark:border-gray-800">
        
        {/* Cabecera */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800 bg-fondoVs dark:bg-gray-800 rounded-t-2xl">
          <div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Ticket className="w-5 h-5 text-purple-600 dark:text-purple-400 animate-pulse" />
              Asignación Rápida Guiada
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Graduado: <strong className="text-gray-900 dark:text-white">{invitado.nombre}</strong> • Boletos: <strong>{totalBoletos}</strong>
            </p>
          </div>
          <Button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Stepper progress indicator */}
        <div className="px-5 py-4 bg-gray-50 dark:bg-gray-850 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center overflow-x-auto gap-2">
          {stepsLabels.map((label, idx) => (
            <div key={idx} className="flex items-center gap-2 flex-shrink-0">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  step === idx
                    ? "bg-purple-600 text-white ring-4 ring-purple-100 dark:ring-purple-900/30"
                    : step > idx
                    ? "bg-green-500 text-white"
                    : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                }`}
              >
                {step > idx ? "✓" : idx + 1}
              </div>
              <span
                className={`text-xs font-semibold whitespace-nowrap ${
                  step === idx
                    ? "text-purple-600 dark:text-purple-400 font-bold"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {label}
              </span>
              {idx < stepsLabels.length - 1 && (
                <div className="w-6 h-0.5 bg-gray-200 dark:bg-gray-700 hidden sm:block"></div>
              )}
            </div>
          ))}
        </div>

        {/* Contenido dinámico por paso */}
        <div className="p-6 flex-1 overflow-y-auto min-h-[300px]">
          
          {/* PASO 1: splits */}
          {step === 0 && (
            <div className="space-y-6">
              <div className="p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-xl">
                <h4 className="font-bold text-purple-950 dark:text-purple-200 text-base mb-1">
                  1. ¿Cómo deseas dividir los {totalBoletos} boletos?
                </h4>
                <p className="text-sm text-purple-900 dark:text-purple-400">
                  El graduado indica cómo separar sus lugares. Cada separación corresponderá a una mesa diferente.
                </p>
              </div>

              {/* Botón rápido para dividir equitativamente */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => aplicarDivisionEquitativa(10)}
                  className="px-3 py-1.5 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 transition-colors"
                >
                  Dividir en mesas de 10
                </button>
                {totalBoletos > 12 && (
                  <button
                    onClick={() => aplicarDivisionEquitativa(12)}
                    className="px-3 py-1.5 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    Dividir en mesas de 12
                  </button>
                )}
                {/* Solo mostrar "Todo en una mesa" si caben en una (máx 12) */}
                {totalBoletos <= MAX_POR_MESA && (
                  <button
                    onClick={() => setSplits([totalBoletos])}
                    className="px-3 py-1.5 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    Todo en una sola mesa
                  </button>
                )}
              </div>

              {/* Listado de divisiones */}
              <div className="space-y-3">
                {splits.map((valor, index) => {
                  const excedeLimite = valor > MAX_POR_MESA;
                  return (
                    <div key={index} className={`flex flex-col gap-1.5 bg-gray-50 dark:bg-gray-800/40 p-3 rounded-xl border transition-colors ${
                      excedeLimite
                        ? "border-red-300 dark:border-red-700 bg-red-50/40 dark:bg-red-950/10"
                        : "border-gray-200/60 dark:border-gray-800"
                    }`}>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-gray-500 dark:text-gray-400 w-16">
                          Mesa {index + 1}:
                        </span>
                        
                        {/* Controles de incremento */}
                        <div className="flex items-center gap-2 flex-1">
                          <Button
                            onClick={() => handleSplitValueChange(index, valor - 1)}
                            disabled={valor <= 1}
                            className="p-1 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <Minus className="w-3.5 h-3.5" />
                          </Button>
                          <input
                            type="number"
                            min="1"
                            max={MAX_POR_MESA}
                            value={valor || ""}
                            onChange={(e) => handleSplitValueChange(index, e.target.value)}
                            className={`w-20 px-3 py-1 border rounded-md text-center text-sm font-bold dark:bg-gray-700 dark:text-white ${
                              excedeLimite
                                ? "border-red-400 dark:border-red-600 text-red-700 dark:text-red-400"
                                : "border-gray-300 dark:border-gray-600"
                            }`}
                          />
                          <Button
                            onClick={() => handleSplitValueChange(index, valor + 1)}
                            disabled={sumaSplits >= totalBoletos || valor >= MAX_POR_MESA}
                            className="p-1 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </Button>
                          <span className="text-xs text-gray-500">boletos</span>
                        </div>

                        {splits.length > 1 && (
                          <button
                            onClick={() => handleRemoveSplit(index)}
                            className="text-red-500 hover:text-red-700 dark:hover:text-red-400 p-1.5 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-colors"
                            title="Eliminar esta mesa"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {/* Advertencia de límite por fila */}
                      {excedeLimite && (
                        <p className="text-xs text-red-600 dark:text-red-400 font-semibold flex items-center gap-1 pl-1">
                          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                          Máximo {MAX_POR_MESA} boletos por mesa. Divide este grupo en 2 o más.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Botón para añadir división */}
              <button
                onClick={handleAddSplit}
                disabled={sumaSplits >= totalBoletos}
                className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-dashed border-2 border-dashed border-gray-300 dark:border-gray-700 hover:border-purple-500 hover:text-purple-600 text-gray-500 rounded-xl transition-all font-semibold text-sm hover:bg-purple-50/50 dark:hover:bg-purple-950/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Añadir otra mesa/división
              </button>

              {/* Advertencia de límite global */}
              {algutSplitExcede && sumaSplits === totalBoletos && (
                <div className="p-3 rounded-xl border bg-red-50 border-red-200 text-red-700 dark:bg-red-950/20 dark:border-red-800 dark:text-red-300 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span className="text-xs font-semibold">
                    Uno o más grupos superan el máximo de {MAX_POR_MESA} boletos por mesa. Reduce esos grupos antes de continuar.
                  </span>
                </div>
              )}

              {/* Validación de suma */}
              <div className={`p-4 rounded-xl border flex items-center gap-3 ${
                splitsValidos
                  ? "bg-green-50 border-green-200 text-green-800 dark:bg-green-950/20 dark:border-green-800 dark:text-green-300"
                  : "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/20 dark:border-amber-800 dark:text-amber-300"
              }`}>
                {splitsValidos ? (
                  <>
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-semibold">
                      Listo: Has distribuido exactamente los {totalBoletos} boletos en grupos válidos.
                    </span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 animate-bounce" />
                    <span className="text-sm font-semibold">
                      {sumaSplits !== totalBoletos
                        ? `Suma actual: ${sumaSplits} de ${totalBoletos} boletos. ${sumaSplits < totalBoletos ? `Faltan ${totalBoletos - sumaSplits} boletos.` : `Te has excedido por ${sumaSplits - totalBoletos}.`}`
                        : `Corrige los grupos que superan el límite de ${MAX_POR_MESA} por mesa.`
                      }
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* PASO 2: Selección de Mesas */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-xl flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h4 className="font-bold text-purple-950 dark:text-purple-200 text-base mb-1">
                    2. ¿En qué mesas irá cada separación de boletos?
                  </h4>
                  <p className="text-sm text-purple-900 dark:text-purple-400">
                    Selecciona la mesa física para cada grupo. Puedes ver el salón antes de elegir.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {splits.map((cantidad, index) => {
                  const mesaIdSeleccionada = mesasSeleccionadas[index];
                  const mesaObj = mesasDisponibles.find(m => m.id === mesaIdSeleccionada);
                  const sobrecarga = mesaObj && cantidad > mesaObj.libres;
                  const textoBusqueda = (busquedaMesas[index] || "").toLowerCase().trim();

                  // Filtrar mesas según texto de búsqueda
                  const mesasFiltradas = textoBusqueda
                    ? mesasDisponibles.filter(m =>
                        String(m.numero).includes(textoBusqueda) ||
                        (m.nombre || "").toLowerCase().includes(textoBusqueda)
                      )
                    : mesasDisponibles;

                  return (
                    <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-xs px-2.5 py-1 rounded-full font-bold">
                          Grupo {index + 1}
                        </span>
                        <strong className="text-sm text-gray-700 dark:text-gray-300">
                          {cantidad} boletos
                        </strong>
                      </div>

                      {/* Buscador de mesa */}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Buscar mesa por número..."
                          value={busquedaMesas[index] || ""}
                          onChange={(e) =>
                            setBusquedaMesas((prev) => ({ ...prev, [index]: e.target.value }))
                          }
                          className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 pl-8 pr-3 py-2 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                      </div>

                      {/* Selector de Mesa */}
                      <select
                        value={mesaIdSeleccionada}
                        onChange={(e) => handleMesaChange(index, e.target.value)}
                        size={Math.min(mesasFiltradas.length + 1, 7)}
                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-medium"
                      >
                        <option value="">-- Selecciona Mesa --</option>
                        {mesasFiltradas.map((m) => {
                          const estaSeleccionadaEnOtroSplit = mesasSeleccionadas.some((selectedId, idx) => selectedId === m.id && idx !== index);
                          
                          // ✅ Una mesa NO está disponible si:
                          // 1. Está bloqueada
                          // 2. Está seleccionada en otro split
                          // 3. Los ocupados + boletos de este split superan el límite REAL del sistema (MAX_POR_MESA=12)
                          //    NOTA: m.capacidad puede ser 10 (default), pero el techo real es MAX_POR_MESA
                          const limiteReal = Math.max(m.capacidad, MAX_POR_MESA);
                          const noCalenLosboletos = (m.ocupados + cantidad) > limiteReal;
                          const noDisponible = m.estaBloqueada || estaSeleccionadaEnOtroSplit || noCalenLosboletos;

                          let sufijo = "";
                          if (m.estaBloqueada) sufijo = ` 🔒 BLOQUEADA`;
                          else if (estaSeleccionadaEnOtroSplit) sufijo = " (en otro grupo)";
                          else if (noCalenLosboletos) sufijo = ` ✗ SIN ESPACIO (${m.ocupados}/${limiteReal}, necesitas ${cantidad})`;
                          else sufijo = ` — ${limiteReal - m.ocupados} libres`;

                          const labelMesa = `Mesa ${m.numero}${sufijo}`;

                          return (
                            <option
                              key={m.id}
                              value={m.id}
                              disabled={noDisponible}
                              className={noDisponible ? "text-gray-400 dark:text-gray-600" : ""}
                            >
                              {labelMesa}
                            </option>
                          );
                        })}
                      </select>

                      {/* Advertencia de capacidad */}
                      {sobrecarga && (
                        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 px-3 py-2.5 rounded-xl text-xs flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong className="block mb-0.5">⚠️ Capacidad Excedida</strong>
                            La Mesa {mesaObj.numero} tiene {mesaObj.libres} asientos libres, pero asignas {cantidad}. Se forzará el límite.
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* PASO 3: Menús Especiales */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-xl">
                <h4 className="font-bold text-purple-950 dark:text-purple-200 text-base mb-1">
                  3. ¿Todos los menús serán normales o tendrán infantiles/especiales?
                </h4>
                <p className="text-sm text-purple-900 dark:text-purple-400">
                  Por cada grupo asignado, indica si todos consumirán menú estándar o detalla las excepciones.
                </p>
              </div>

              <div className="space-y-5">
                {splits.map((cantidad, index) => {
                  const mesaId = mesasSeleccionadas[index];
                  const mesaObj = mesasDisponibles.find(m => m.id === mesaId);
                  const menuConfig = menusPorSplit[index];

                  // Calcular cantidad normales restante (excluir 'normal' de otros si existiera)
                  const especialesTotal = (menuConfig.infantil || 0) +
                                         (menuConfig.especial || 0) +
                                         (menuConfig.celiaco || 0) +
                                         Object.entries(menuConfig.otros || {})
                                           .filter(([k]) => k !== "normal")
                                           .reduce((s, [, v]) => s + v, 0);
                  const normalesCalculados = cantidad - especialesTotal;

                  return (
                    <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-700 pb-3">
                        <div className="flex items-center gap-2">
                          <span className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-xs px-2.5 py-1 rounded-full font-bold">
                            Mesa {mesaObj?.numero || `Grupo ${index + 1}`}
                          </span>
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {cantidad} personas asignadas
                          </span>
                        </div>

                        {/* Toggle de menú simple */}
                        <div className="flex gap-2 bg-gray-100 dark:bg-gray-900 rounded-lg p-1">
                          <button
                            onClick={() => handleToggleTodosNormales(index, true)}
                            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                              menuConfig.todosNormales
                                ? "bg-white dark:bg-gray-700 shadow text-purple-600 dark:text-purple-300"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
                            }`}
                          >
                            Todos Normales
                          </button>
                          <button
                            onClick={() => handleToggleTodosNormales(index, false)}
                            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                              !menuConfig.todosNormales
                                ? "bg-white dark:bg-gray-700 shadow text-purple-600 dark:text-purple-300"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
                            }`}
                          >
                            Con Excepciones
                          </button>
                        </div>
                      </div>

                      {/* Configuración de menús con excepciones */}
                      {!menuConfig.todosNormales ? (
                        <div className="space-y-4 pt-2">
                          {/* Resumen de cálculo */}
                          <div className="flex justify-between items-center text-xs bg-gray-50 dark:bg-gray-900 p-2.5 rounded-lg border border-gray-100 dark:border-gray-800 font-medium">
                            <span className="text-gray-600 dark:text-gray-400">Distribución de platos:</span>
                            <span className="text-gray-900 dark:text-white">
                              🍽️ Normales: <strong className="text-green-600 dark:text-green-400">{normalesCalculados}</strong> | 
                              🧒 Especiales: <strong className="text-purple-600 dark:text-purple-400">{especialesTotal}</strong>
                            </span>
                          </div>

                          {/* Opciones de incremento para menús especiales (Normal se calcula solo) */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {opcionesMenuEspeciales.map((opc) => {
                              const valor = opc.id === "infantil" ? menuConfig.infantil :
                                            opc.id === "especial" ? menuConfig.especial :
                                            opc.id === "celiaco" ? menuConfig.celiaco :
                                            (menuConfig.otros?.[opc.id] || 0);

                              return (
                                <div key={opc.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-900/10">
                                  <div className="flex items-center gap-2">
                                    <ChefHat className="w-4 h-4 text-purple-500" />
                                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                      {opc.label}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      onClick={() => handleMenuCountChange(index, opc.id, -1)}
                                      disabled={valor === 0}
                                      className="p-1 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 dark:bg-gray-850 dark:border-gray-600"
                                    >
                                      <Minus className="w-3 h-3" />
                                    </Button>
                                    <span className="w-6 text-center text-xs font-bold dark:text-white">
                                      {valor}
                                    </span>
                                    <Button
                                      onClick={() => handleMenuCountChange(index, opc.id, 1)}
                                      disabled={normalesCalculados === 0}
                                      className="p-1 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 dark:bg-gray-850 dark:border-gray-600"
                                    >
                                      <Plus className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 font-semibold bg-green-50 dark:bg-green-950/20 p-3 rounded-xl">
                          <CheckCircle className="w-4 h-4" />
                          <span>Todos los menús serán normales (estándar) para esta mesa.</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* PASO 4: Detalles, Alergias y Accesibilidad */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-xl">
                <h4 className="font-bold text-purple-950 dark:text-purple-200 text-base mb-1">
                  4. ¿Desea agregar algún detalle en cada mesa? (alergias, condimentos, etc.)
                </h4>
                <p className="text-sm text-purple-900 dark:text-purple-400">
                  Registra comentarios de alergias o si algún invitado del grupo requiere silla de ruedas.
                </p>
              </div>

              <div className="space-y-5">
                {splits.map((cantidad, index) => {
                  const mesaId = mesasSeleccionadas[index];
                  const mesaObj = mesasDisponibles.find(m => m.id === mesaId);
                  const detalleConfig = detallesPorSplit[index];

                  return (
                    <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm space-y-4">
                      <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-700 pb-2">
                        <span className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-xs px-2.5 py-1 rounded-full font-bold">
                          Mesa {mesaObj?.numero || `Grupo ${index + 1}`}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({cantidad} boletos)
                        </span>
                      </div>

                      {/* Detalle o Alergias */}
                      <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                          Alergias o comentarios dietéticos
                        </label>
                        <input
                          type="text"
                          value={detalleConfig.notas}
                          onChange={(e) => handleNotaChange(index, e.target.value)}
                          placeholder="Ej: 1 Alergia a mariscos, sin cebolla, intolerancia a lactosa..."
                          className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5 text-xs dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>

                      {/* Switch rápido de accesibilidad */}
                      <label className={`flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition-all ${
                        detalleConfig.requiereAccesibilidad
                          ? "bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-950/20 dark:border-blue-800 dark:text-blue-200"
                          : "border-transparent hover:bg-gray-50 dark:hover:bg-gray-750"
                      }`}>
                        <input
                          type="checkbox"
                          checked={detalleConfig.requiereAccesibilidad}
                          onChange={(e) => handleAccesibilidadChange(index, e.target.checked)}
                          className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <Accessibility className={`w-4 h-4 ${detalleConfig.requiereAccesibilidad ? "text-blue-600" : "text-gray-400"}`} />
                        <span className="text-xs font-semibold">
                          ♿ Alguien en este grupo requiere silla de ruedas / acceso especial
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* PASO 5: Resumen de Asignación */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-green-950 dark:text-green-200 text-base">
                    5. Resumen final de la asignación
                  </h4>
                  <p className="text-sm text-green-900 dark:text-green-400">
                    Confirma que la distribución de mesas, menús y notas sea correcta antes de guardar.
                  </p>
                </div>
              </div>

              {/* Fichas de Resumen */}
              <div className="space-y-4">
                {splits.map((cantidad, index) => {
                  const mesaId = mesasSeleccionadas[index];
                  const mesaObj = mesasDisponibles.find(m => m.id === mesaId);
                  const menuConfig = menusPorSplit[index];
                  const detalleConfig = detallesPorSplit[index];

                  // Calcular normales (excluir 'normal' de otros si existiera)
                  const especialesTotal = (menuConfig.infantil || 0) +
                                         (menuConfig.especial || 0) +
                                         (menuConfig.celiaco || 0) +
                                         Object.entries(menuConfig.otros || {})
                                           .filter(([k]) => k !== "normal")
                                           .reduce((s, [, v]) => s + v, 0);
                  const normalesCalculados = cantidad - especialesTotal;

                  return (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/40 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-2">
                        <span className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2">
                          <Home className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          Mesa Asignada: <span className="text-purple-600 dark:text-purple-400 font-extrabold text-base">Mesa {mesaObj?.numero}</span>
                        </span>
                        <span className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300 text-xs px-2.5 py-0.5 rounded-full font-semibold">
                          {cantidad} personas
                        </span>
                      </div>

                      {/* Detalle de Platos */}
                      <div className="flex flex-wrap gap-3 text-xs">
                        <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
                          🍽️ Normales: <strong>{normalesCalculados}</strong>
                        </span>
                        {menuConfig.infantil > 0 && (
                          <span className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 font-semibold">
                            🧒 Infantil: <strong>{menuConfig.infantil}</strong>
                          </span>
                        )}
                        {menuConfig.especial > 0 && (
                          <span className="flex items-center gap-1.5 text-yellow-600 dark:text-yellow-400 font-semibold">
                            ⭐ Especial: <strong>{menuConfig.especial}</strong>
                          </span>
                        )}
                        {menuConfig.celiaco > 0 && (
                          <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-semibold">
                            🌾 Celíaco: <strong>{menuConfig.celiaco}</strong>
                          </span>
                        )}
                        {/* Mostrar otros tipos de menú especiales (excluyendo 'normal') */}
                        {Object.entries(menuConfig.otros || {})
                          .filter(([k, v]) => k !== "normal" && v > 0)
                          .map(([k, v]) => (
                            <span key={k} className="flex items-center gap-1.5 text-orange-600 dark:text-orange-400 font-semibold">
                              🍱 {k.charAt(0).toUpperCase() + k.slice(1)}: <strong>{v}</strong>
                            </span>
                          ))}
                      </div>

                      {/* Detalles Extra */}
                      {(detalleConfig.notas || detalleConfig.requiereAccesibilidad) && (
                        <div className="bg-white dark:bg-gray-850 p-2.5 rounded-lg border border-gray-100 dark:border-gray-800 space-y-1.5">
                          {detalleConfig.notas && (
                            <p className="text-xs text-gray-700 dark:text-gray-350 flex items-start gap-1">
                              <Utensils className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                              <span><strong>Detalles:</strong> {detalleConfig.notas}</span>
                            </p>
                          )}
                          {detalleConfig.requiereAccesibilidad && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 font-semibold">
                              <Accessibility className="w-3.5 h-3.5" />
                              <span>Requiere accesibilidad de silla de ruedas</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {/* Footer de Botones */}
        <div className="flex justify-between items-center p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
          <Button
            onClick={() => setStep(step - 1)}
            disabled={step === 0}
            className="px-4 py-2 text-sm font-semibold rounded-xl border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600 transition-colors flex items-center gap-1.5"
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </Button>

          <div className="flex gap-3">
            <Button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold rounded-xl border border-gray-300 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </Button>
            
            {step < 4 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 0 && !splitsValidos) ||
                  (step === 1 && !mesasValidas) ||
                  (step === 2 && !menusValidos)
                }
                className="px-5 py-2 text-sm font-semibold rounded-xl bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5 shadow-md hover:shadow-lg"
              >
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleConfirmar}
                className="px-6 py-2 text-sm font-bold rounded-xl bg-green-600 text-white hover:bg-green-700 transition-colors shadow-md hover:shadow-lg flex items-center gap-1.5"
              >
                Confirmar y Guardar
                <CheckCircle className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

      </div>



    </div>
  );
}
